import type { ReactNode } from "react";
import { Icon } from "../icons";
import { cn } from "../utils";
import { useCopy } from "./use-copy";

/** Props Interface */
interface CopyProps {
	/** What lands in the clipboard; null renders the children alone, without the button. */
	value: string | null | undefined;
	/** What is shown; defaults to the value. */
	children?: ReactNode;
	className?: string;
}

export function Copy({ value, children, className }: CopyProps) {
	/** Hooks */
	const { isCopied, copy } = useCopy(value);

	/** Guard */
	if (!value) return children ?? null;

	/** Render */
	// The negative margins cancel the hover padding, so the text sits where plain text would — which
	// needs a parent that does not clip: `DataGrid.Cell`, not the truncating `DataGrid.Value`.
	return (
		<button
			type="button"
			onClick={copy}
			className={cn(
				"group -mx-1.5 -my-0.5 inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-0.5 text-left transition-colors hover:bg-emerald-500/10 hover:text-emerald-600 dark:hover:text-emerald-400",
				className,
			)}
		>
			<span className="truncate">{children ?? value}</span>
			<span className="inline-flex shrink-0 opacity-40 transition-opacity group-hover:opacity-100">
				<Icon name={isCopied ? "Check" : "Copy"} />
			</span>
		</button>
	);
}
